import LoginTranslations from "./translations";

export interface IPasswordPolicy {
    minimumLength: number;
    isUpperCaseRequired: boolean;
    isLowerCaseRequired: boolean;
    isNumericRequired: boolean;
    isNonAlphanumericRequired: boolean;
}

export interface IPasswordRule {
    id: string;
    /** May hold a {0} for count. */
    sentence: string;
    count?: number;
    /** Undefined while the rule cannot be checked. */
    isMet: (password: string) => boolean | undefined;
}

const isDigit = (character: string) => !isNaN(parseInt(character, 10));
const isUpperCase = (character: string) => !isDigit(character) && character !== character.toLowerCase();
const isLowerCase = (character: string) => !isDigit(character) && character !== character.toUpperCase();
const isSpecial = (character: string) => !isDigit(character) && character.toLowerCase() === character.toUpperCase();
const contains = (isCharacter: (character: string) => boolean) => (password: string) => password.split("").some(isCharacter);

/**
 * The rules a new password must meet, in the order MyGeotab lists them.
 * The common password rule can only be checked once its list is passed.
 */
export function getPasswordRules(policy: IPasswordPolicy, userNames: (string | undefined)[], commonPasswords?: ReadonlySet<string>): IPasswordRule[] {
    // phone numbers are compared without their country code
    const names = userNames
        .filter((name): name is string => !!name)
        .map(name => name.startsWith("+") ? name.substring(name.indexOf(" ") + 1) : name.toLowerCase());
    const isNotUserName = (password: string) => {
        const lowerCasePassword = password.toLowerCase();
        return names.every(name => !lowerCasePassword.includes(name)) && !names.join().includes(lowerCasePassword);
    };
    const rules: (IPasswordRule | false)[] = [
        policy.minimumLength !== 0 && {
            id: "minimum-length-policy",
            sentence: LoginTranslations.PasswordMinimumLength,
            count: policy.minimumLength,
            isMet: password => password.length >= policy.minimumLength
        },
        policy.isUpperCaseRequired && { id: "uppercase-required-policy", sentence: LoginTranslations.PasswordUpperCase, count: 1, isMet: contains(isUpperCase) },
        policy.isLowerCaseRequired && { id: "lowercase-required-policy", sentence: LoginTranslations.PasswordLowerCase, count: 1, isMet: contains(isLowerCase) },
        policy.isNumericRequired && { id: "numeric-required-policy", sentence: LoginTranslations.PasswordNumbers, count: 1, isMet: contains(isDigit) },
        policy.isNonAlphanumericRequired && { id: "special-required-policy", sentence: LoginTranslations.PasswordSpecialCharacters, count: 1, isMet: contains(isSpecial) },
        { id: "not-username-policy", sentence: LoginTranslations.PasswordNotUserName, isMet: isNotUserName },
        // the list is lowercased, like the server compares
        { id: "not-common-policy", sentence: LoginTranslations.PasswordNotCommon, isMet: password => commonPasswords && !commonPasswords.has(password.toLowerCase()) }
    ];

    return rules.filter((rule): rule is IPasswordRule => !!rule);
}
